export type SupportedLocale = string

/** Used only when Crowdin list is empty / unavailable */
const FALLBACK_LOCALES = [
  'en',
  'de',
  'es',
  'fr',
  'pt-br',
  'it',
  'nl',
  'pl',
  'ru',
  'tr',
  'uk',
]

let supportedLocales: string[] = [...FALLBACK_LOCALES]
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((listener) => listener())
}

/** Locales whose region must stay in the path to avoid ambiguity with the base language */
const REGIONAL_LOCALES = new Set(['pt-br'])

function toPathLocale(code: string): string {
  return code.replace('_', '-').toLowerCase()
}

/** Crowdin ships region-qualified ids (en-US, es-ES, sv-SE); paths use the base language */
function toShortLocale(code: string): string {
  const normalized = toPathLocale(code)
  return REGIONAL_LOCALES.has(normalized) ? normalized : normalized.split('-')[0]
}

function uniqueLocales(codes: string[]): string[] {
  const set = new Set<string>()
  set.add('en')
  for (const code of codes) {
    const normalized = toShortLocale(code)
    if (normalized) set.add(normalized)
  }
  return Array.from(set)
}

/** Replace allowlist with Crowdin codes (+ en). Empty input keeps fallback list. */
export function setSupportedLocalesFromCrowdin(crowdinCodes: string[]): void {
  supportedLocales = crowdinCodes.length ? uniqueLocales(crowdinCodes) : [...FALLBACK_LOCALES]
  notify()
}

export function getSupportedLocales(): string[] {
  return supportedLocales
}

export function subscribeSupportedLocales(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function isLocale(value: string | undefined | null): boolean {
  if (!value) return false
  return supportedLocales.includes(toPathLocale(value))
}

export function normalizeLocale(value: string | undefined | null): SupportedLocale {
  if (!value) return 'en'
  const normalized = toPathLocale(value)
  if (isLocale(normalized)) return normalized
  const base = normalized.split('-')[0]
  if (isLocale(base)) return base
  if (base === 'pt' && isLocale('pt-br')) return 'pt-br'
  return 'en'
}

export function getLocaleLabel(code: string): string {
  const normalized = toPathLocale(code)
  const parts = normalized.split('-')
  const intlCode =
    parts.length === 1 ? parts[0] : `${parts[0]}-${parts.slice(1).join('-').toUpperCase()}`

  try {
    const label = new Intl.DisplayNames([intlCode], { type: 'language' }).of(intlCode)
    if (label) return label.charAt(0).toUpperCase() + label.slice(1)
  } catch {
    /* ignore */
  }

  return normalized
}

export function getLocaleFromPath(pathname: string): SupportedLocale | null {
  const segment = pathname.split('/').filter(Boolean)[0]
  if (!segment) return null
  const normalized = toPathLocale(segment)
  return isLocale(normalized) ? normalized : null
}

export function stripLocaleFromPath(pathname: string): string {
  const locale = getLocaleFromPath(pathname)
  if (!locale) return pathname || '/'

  const rest = pathname.replace(new RegExp(`^/${locale}`, 'i'), '')
  return rest.startsWith('/') ? rest : `/${rest}`
}

export function withLocale(pathname: string, locale: SupportedLocale): string {
  const stripped = stripLocaleFromPath(pathname)
  const rest = stripped === '/' ? '' : stripped
  return `/${toShortLocale(locale)}${rest}`
}

export function getScreenPath(pathname: string): string {
  return stripLocaleFromPath(pathname).replace(/^\//, '')
}
